import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Snackbar,
  Stack,
} from '@mui/material';
import {
  AccountBalance as AccountIcon,
  CreditCard as LoanIcon,
  Home as MortgageIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { api } from '../contexts/ApiContext';
import { createToast, inferToastSeverityFromMessage, toastFromApiError, type AppToast } from '../utils/appToast';
import { ToastCodeCaption } from './ToastCodeCaption';
import { useAuth } from '../contexts/AuthContext';
import {
  FIT_TABLE_SX,
  FIT_TABLE_CONTAINER_SX,
  FitColgroup,
  buildFitColgroup,
  fitTruncateSx,
} from '../utils/fitTable';

interface Balance {
  id: number;
  user_id: number;
  account_id?: number;
  loan_id?: number;
  balance: number;
  currency: string;
  notes?: string;
  date_entered: string;
  best_ever_net_position?: boolean;
  created_at: string;
  updated_at: string;
  account_name?: string;
  loan_name?: string;
}

interface Account {
  id: number;
  account_name: string;
  account_type: string;
}

interface Loan {
  id: number;
  loan_name: string;
  loan_type: string;
}

interface BalanceManagementProps {
  onBalanceAdded?: () => void;
}

const BalanceManagement: React.FC<BalanceManagementProps> = ({ onBalanceAdded }) => {
  const { user } = useAuth();
  const [balances, setBalances] = useState<Balance[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loans, setLoans] = useState<Loan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [syncingAmortization, setSyncingAmortization] = useState(false);
  const [toast, setToast] = useState<AppToast | null>(null);

  const showToast = (severity: AppToast['severity'], message: string) => {
    setToast(createToast(severity || inferToastSeverityFromMessage(message), message));
  };

  const handleCloseToast = () => {
    setToast(null);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const [balancesRes, accountsRes, loansRes] = await Promise.all([
        api.get('/api/balances/'),
        api.get('/api/accounts/'),
        api.get('/api/loans/')
      ]);

      setBalances(balancesRes.data.balances || []);
      setAccounts(accountsRes.data.accounts || []);
      setLoans(loansRes.data.loans || []);
    } catch (err: any) {
      console.error('Error fetching balance data:', err);
      setError(err.response?.data?.error || 'Failed to load balance data');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSyncAmortization = useCallback(async () => {
    try {
      setSyncingAmortization(true);
      setError('');

      // Use longer timeout for amortization operations (can take 60+ seconds with multiple loans)
      const longTimeoutConfig = { timeout: 120000 }; // 2 minutes

      // First, create the amortization table if it doesn't exist
      await api.post('/api/migration/create-amortization-table', {}, longTimeoutConfig);
      
      // Generate amortization schedules for all loans (this can take a while)
      await api.post('/api/amortization/generate-schedules', {}, longTimeoutConfig);
      
      // Sync amortization schedules to balances table
      const response = await api.post('/api/amortization/sync-to-balances', {}, longTimeoutConfig);
      
      // Refresh the balance data
      await fetchData();
      
      showToast('success', `Amortization sync completed successfully. ${response.data.message}`);
      
    } catch (err: any) {
      console.error('Error syncing amortization:', err);
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        setError('Amortization sync timed out. This can happen with many loans. Please try again or sync manually.');
        setToast(toastFromApiError(err, 'Amortization sync timed out. This can happen with many loans.'));
      } else {
        setError(err.response?.data?.error || 'Failed to sync amortization schedules');
        setToast(toastFromApiError(err, 'Failed to sync amortization schedules.'));
      }
    } finally {
      setSyncingAmortization(false);
    }
  }, [fetchData]);

  useEffect(() => {
    fetchData();
    // Auto-sync loan and bank account balances on page load
    handleSyncAmortization();
  }, [fetchData, handleSyncAmortization]);

  // RBAC Check - only allow users with balances permission
  if (!user || (user.role !== 'admin' && !user.can_view_balances)) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" component="h1" gutterBottom>
          💰 Account Balances
        </Typography>
        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            Access Restricted
          </Typography>
          <Typography variant="body2">
            You don't have permission to view account balances. Please contact your administrator to request access.
          </Typography>
        </Alert>
      </Box>
    );
  }

  // Helper function to get all Sunday dates in the past (newest first)
  const getAllDates = () => {
    const sundays = [];
    const today = new Date();
    
    // Start from the most recent Sunday
    const lastSunday = new Date(today);
    lastSunday.setDate(today.getDate() - today.getDay());
    
    // Generate Sundays going back (up to 52 weeks = 1 year)
    for (let i = 0; i < 52; i++) {
      const sunday = new Date(lastSunday);
      sunday.setDate(lastSunday.getDate() - (i * 7));
      
      // Only include past dates
      if (sunday <= today) {
        sundays.push(sunday.toISOString().split('T')[0]);
      }
    }
    
    return sundays;
  };

  // Helper function to get balance for specific account/loan on specific date
  const getBalanceForDate = (type: 'account' | 'loan', id: number, date: string) => {
    const balance = balances.find(b => 
      ((type === 'account' && b.account_id === id) || (type === 'loan' && b.loan_id === id)) &&
      b.date_entered === date
    );
    return balance?.balance || 0;
  };

  // Helper function to get latest balance for account/loan
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getLatestBalance = (type: 'account' | 'loan', id: number) => {
    const relevantBalances = balances.filter(b => 
      (type === 'account' && b.account_id === id) || (type === 'loan' && b.loan_id === id)
    );
    if (relevantBalances.length === 0) return 0;
    const latest = relevantBalances.sort((a, b) => new Date(b.date_entered).getTime() - new Date(a.date_entered).getTime())[0];
    return latest.balance;
  };

  // Helper function to calculate net position for a date
  const getNetPosition = (date: string) => {
    let net = 0;
    
    // Add all account balances (positive)
    accounts.forEach(account => {
      net += getBalanceForDate('account', account.id, date);
    });
    
    // Subtract all loan balances (negative - loans are debt)
    loans.forEach(loan => {
      net -= getBalanceForDate('loan', loan.id, date);
    });
    
    return net;
  };

  // Helper function to get net position for last week (7 days ago)
  const getLastWeekNetPosition = (currentDate: string) => {
    const current = new Date(currentDate);
    const lastWeek = new Date(current.getTime() - 7 * 24 * 60 * 60 * 1000);
    const lastWeekStr = lastWeek.toISOString().split('T')[0];
    return getNetPosition(lastWeekStr);
  };

  // Helper function to get best ever net position
  const getBestEverNetPosition = () => {
    const bestEverBalance = balances.find(b => b.best_ever_net_position);
    if (bestEverBalance) {
      return getNetPosition(bestEverBalance.date_entered);
    }
    return 0;
  };

  // Helper function to get last week difference amount
  const getLastWeekDifference = (currentDate: string) => {
    const current = getNetPosition(currentDate);
    const lastWeek = getLastWeekNetPosition(currentDate);
    
    if (lastWeek === 0) return null;
    return current - lastWeek;
  };

  // Helper function to get best ever difference amount
  const getBestEverDifference = (currentDate: string) => {
    const current = getNetPosition(currentDate);
    const bestEver = getBestEverNetPosition();
    
    if (bestEver === 0) return null;
    return current - bestEver;
  };



  const formatCurrency = (amount: number, currency: string) => {
    // Handle invalid or empty currency codes
    const validCurrency = currency && currency.trim() !== '' ? currency : 'EUR';
    
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: validCurrency,
        minimumFractionDigits: 2
      }).format(amount);
    } catch (error) {
      // Fallback to EUR if currency is invalid
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2
      }).format(amount);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" component="h2">
              <TrendingUpIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Balances
            </Typography>
            <Button
              variant="outlined"
              onClick={handleSyncAmortization}
              disabled={syncingAmortization}
              color="secondary"
              sx={{ minWidth: 200 }}
            >
              {syncingAmortization ? 'Syncing...' : 'Sync Loan & Bank Account Balances'}
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Balance Overview Table */}
          <TableContainer component={Paper} variant="outlined" sx={{ ...FIT_TABLE_CONTAINER_SX, maxHeight: '70vh' }}>
            <Table stickyHeader size="small" sx={FIT_TABLE_SX}>
              <FitColgroup
                cols={buildFitColgroup([
                  { key: 'date', pct: 10 },
                  ...accounts.map((account) => ({ key: `account-${account.id}` })),
                  ...loans.map((loan) => ({ key: `loan-${loan.id}` })),
                  { key: 'net', pct: 11 },
                  { key: 'vs-week', pct: 10 },
                  { key: 'vs-best', pct: 10 },
                ])}
              />
              <TableHead>
                <TableRow>
                  <TableCell><strong>Date</strong></TableCell>
                  {/* Account columns */}
                  {accounts.map((account) => (
                    <TableCell key={`account-${account.id}`} align="center">
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                        <AccountIcon fontSize="small" />
                        <Box component="strong" sx={fitTruncateSx} title={account.account_name}>
                          {account.account_name}
                        </Box>
                      </Box>
                    </TableCell>
                  ))}
                  {/* Loan columns */}
                  {loans.map((loan) => {
                    // Determine icon and clean text based on loan type
                    const isMortgage = loan.loan_type === 'mortgage';
                    const icon = isMortgage ? <MortgageIcon fontSize="small" /> : <LoanIcon fontSize="small" />;
                    
                    // Clean up the loan name by removing redundant prefixes
                    let cleanName = loan.loan_name;
                    if (cleanName.includes('Loan from ')) {
                      cleanName = cleanName.replace('Loan from ', '');
                    } else if (cleanName.includes('Mortgage with ')) {
                      cleanName = cleanName.replace('Mortgage with ', '');
                    }
                    
                    return (
                      <TableCell key={`loan-${loan.id}`} align="center">
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                          {icon}
                          <Box component="strong" sx={fitTruncateSx} title={loan.loan_name}>
                            {cleanName}
                          </Box>
                        </Box>
                      </TableCell>
                    );
                  })}
                  <TableCell align="right" sx={{ backgroundColor: 'primary.light', color: 'primary.contrastText' }}>
                    <strong>Net Position</strong>
                  </TableCell>
                  <TableCell align="center">
                    <strong>vs Last Week</strong>
                  </TableCell>
                  <TableCell align="center">
                    <strong>vs Best Ever</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getAllDates().map((date) => (
                    <TableRow key={date} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {formatDate(date)}
                        </Typography>
                      </TableCell>
                      {/* Account balance cells */}
                      {accounts.map((account) => {
                        const balance = getBalanceForDate('account', account.id, date);
                        return (
                          <TableCell 
                            key={`account-${account.id}-${date}`} 
                            align="right"
                          >
                            <Typography 
                              variant="body2" 
                              fontWeight="medium"
                              color={balance >= 0 ? 'success.main' : 'error.main'}
                            >
                              {formatCurrency(balance, 'EUR')}
                            </Typography>
                          </TableCell>
                        );
                      })}
                      {/* Loan balance cells */}
                      {loans.map((loan) => {
                        const balance = getBalanceForDate('loan', loan.id, date);
                        return (
                          <TableCell 
                            key={`loan-${loan.id}-${date}`} 
                            align="right"
                          >
                            <Typography 
                              variant="body2" 
                              fontWeight="medium"
                              color="error.main"
                            >
                              {formatCurrency(balance, 'EUR')}
                            </Typography>
                          </TableCell>
                        );
                      })}
                      {/* Net position cell */}
                      <TableCell 
                        align="right"
                        sx={{ 
                          backgroundColor: 'primary.light', 
                          color: 'primary.contrastText',
                          fontWeight: 'bold'
                        }}
                      >
                        <Typography variant="body2" fontWeight="bold">
                          {formatCurrency(getNetPosition(date), 'EUR')}
                        </Typography>
                      </TableCell>
                      {/* vs Last Week comparison cell */}
                      <TableCell align="center">
                        {(() => {
                          const difference = getLastWeekDifference(date);
                          if (difference === null) {
                            return <Typography variant="body2" color="text.secondary">N/A</Typography>;
                          }
                          return (
                            <Typography 
                              variant="body2" 
                              fontWeight="bold"
                              color={difference > 0 ? 'success.main' : difference < 0 ? 'error.main' : 'text.secondary'}
                            >
                              {difference > 0 ? '+' : ''}{formatCurrency(difference, 'EUR')}
                            </Typography>
                          );
                        })()}
                      </TableCell>
                      {/* vs Best Ever comparison cell */}
                      <TableCell align="center">
                        {(() => {
                          const difference = getBestEverDifference(date);
                          if (difference === null) {
                            return <Typography variant="body2" color="text.secondary">N/A</Typography>;
                          }
                          return (
                            <Typography 
                              variant="body2" 
                              fontWeight="bold"
                              color={difference > 0 ? 'success.main' : difference < 0 ? 'error.main' : 'text.secondary'}
                            >
                              {difference > 0 ? '+' : ''}{formatCurrency(difference, 'EUR')}
                            </Typography>
                          );
                        })()}
                      </TableCell>
                    </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Toast Notification */}
      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseToast} severity={toast?.severity || 'info'} sx={{ width: '100%' }}>
          <Stack spacing={0.25}>
            <Typography variant="body2">{toast?.message}</Typography>
            <ToastCodeCaption
              code={toast?.diagnostics.code || ''}
              requestId={toast?.diagnostics.requestId}
              jobId={toast?.diagnostics.jobId}
              errorCode={toast?.diagnostics.errorCode}
              copyBody={toast?.message}
            />
          </Stack>
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default BalanceManagement;
